const normalize = value => (value || '').trim().toLowerCase();
const circadiaConfigurations = new Map([
  ['wood/oak', 'finish_oak'],
  ['wood/ash', 'finish_ash'],
  ['wood/walnut', 'finish_walnut'],
  ['anodized aluminum/clear', 'finish_clear'],
  ['anodized aluminum/blue', 'finish_blue']
]);

export function circadiaConfiguration(material, finish) {
  return circadiaConfigurations.get(`${normalize(material)}/${normalize(finish)}`) || '';
}

export function galleryForFinish(entries, material, finish) {
  const shared = entries.filter(entry => !entry.material && !entry.finish);
  const matching = entries.filter(entry => (entry.material || entry.finish)
    && (!entry.material || normalize(entry.material) === normalize(material))
    && (!entry.finish || normalize(entry.finish) === normalize(finish)));
  return [...matching, ...shared];
}

export function galleryForVariant(entries, variantId, imageId) {
  const visible = entries.filter(entry => !entry.variantIds?.length || entry.variantIds.some(id => String(id) === String(variantId)));
  const primary = visible.find(entry => imageId && String(entry.imageId) === String(imageId));
  return primary ? [primary, ...visible.filter(entry => entry !== primary)] : visible;
}

export function previewInquiryHref(href, selectedId, location) {
  const url = new URL(href, location.href);
  const view = url.searchParams.get('view');
  const previewInquiry = /\/pages\/contact\/?$/.test(url.pathname)
    && ['oyster-product-inquiry', 'oyster-trade'].includes(view);
  const permanentTrade = /\/pages\/for-business\/?$/.test(url.pathname)
    && (view === null || ['for-business', 'oyster-trade'].includes(view));
  if (url.origin !== location.origin || (!previewInquiry && !permanentTrade)
    || url.searchParams.get('collection') !== 'circadia') return href;
  if ([...circadiaConfigurations.values()].includes(selectedId)) url.searchParams.set('configuration', selectedId);
  else url.searchParams.delete('configuration');
  return url.pathname + url.search + url.hash;
}

if (typeof customElements !== 'undefined' && !customElements.get('oyster-collection-product')) {
  customElements.define('oyster-collection-product', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.select = this.querySelector('[data-preview-finish], [data-variant-select]');
      this.entries = Array.from(this.querySelectorAll('[data-gallery-panel]')).map(panel => ({
        id: panel.dataset.galleryPanel,
        material: panel.dataset.galleryMaterial || '',
        finish: panel.dataset.galleryFinish || '',
        imageId: panel.dataset.galleryImageId || '',
        variantIds: JSON.parse(panel.dataset.galleryVariants || '[]'),
        panel
      }));
      this.controls = this.querySelector('[data-gallery-controls]');
      this.pagination = this.querySelector('[data-gallery-pagination]');
      this.position = this.querySelector('[data-gallery-position]');
      this.classList.add('is-enhanced');
      this.select?.addEventListener('change', () => this.updateSelection());
      this.addEventListener('oyster:variant-change', () => this.updateSelection());
      this.updateSelection();
      this.addEventListener('click', event => {
        if (event.target.closest('[data-gallery-previous]')) this.advance(-1);
        if (event.target.closest('[data-gallery-next]')) this.advance(1);
      });
      this.controls.addEventListener('keydown', event => {
        const index = this.visibleEntries.findIndex(entry => entry.id === this.currentId);
        if (index < 0) return;
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % this.visibleEntries.length;
        else if (event.key === 'ArrowLeft') next = (index - 1 + this.visibleEntries.length) % this.visibleEntries.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.visibleEntries.length - 1;
        else return;
        event.preventDefault();
        this.selectView(this.visibleEntries[next].id);
      });
      this.dock = this.querySelector('[data-product-dock]');
      if (this.dock && 'IntersectionObserver' in window) {
        this.dockObserver = new IntersectionObserver(([entry]) => { this.dock.hidden = !entry.isIntersecting; });
        this.dockObserver.observe(this);
      }
      this.addEventListener('shopify:block:select', event => {
        const entry = this.entries.find(item => item.id === event.detail?.blockId);
        if (entry) {
          if (!this.visibleEntries.includes(entry)) this.visibleEntries = [...this.visibleEntries, entry];
          this.selectView(entry.id);
        }
      });
    }

    disconnectedCallback() {
      this.dockObserver?.disconnect();
    }

    updateSelection() {
      const option = this.select?.selectedOptions[0];
      if (this.select?.matches('[data-preview-finish]') && option) {
        const price = this.querySelector('[data-preview-price]');
        if (price && option.dataset.price) price.textContent = option.dataset.price;
      }
      const configuration = circadiaConfiguration(option?.dataset.material, option?.dataset.finish);
      this.querySelectorAll('a[href]').forEach(link => {
        const href = previewInquiryHref(link.href, configuration, window.location);
        if (href !== link.href) link.href = href;
      });
      const matching = galleryForFinish(this.entries, option?.dataset.material, option?.dataset.finish);
      this.visibleEntries = galleryForVariant(matching, option?.value, option?.dataset.imageId);
      const hasVariantPhoto = this.visibleEntries.some(entry => option?.dataset.imageId && entry.imageId === option.dataset.imageId);
      if (!hasVariantPhoto && option?.dataset.imageUrl) {
        this.updateVariantPhoto(option);
        this.visibleEntries.unshift(this.variantEntry);
      }
      const dockPrice = this.querySelector('[data-dock-price]');
      const dockChoice = this.querySelector('[data-dock-choice]');
      if (dockPrice && option?.dataset.price) dockPrice.textContent = option.dataset.price;
      if (dockChoice && option) dockChoice.textContent = option.dataset.title === 'Default Title' ? '' : option.dataset.title || [option.dataset.material, option.dataset.finishLabel || option.dataset.finish].filter(Boolean).join(' · ');
      this.selectView(this.visibleEntries[0]?.id);
      this.controls.hidden = this.visibleEntries.length < 2;
      this.pagination.hidden = this.visibleEntries.length < 2;
    }

    updateVariantPhoto(option) {
      if (!option?.dataset.imageUrl) return;
      if (!this.variantEntry) {
        const panel = document.createElement('figure');
        panel.className = 'oy-collection-product__figure oy-product__figure';
        panel.id = `${this.select.id}-selected-image`;
        const media = document.createElement('div');
        media.className = 'oy-collection-product__media oy-product__media';
        const img = document.createElement('img');
        img.width = 1600;
        img.height = 1600;
        img.decoding = 'async';
        img.addEventListener('load', () => {
          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            img.width = img.naturalWidth;
            img.height = img.naturalHeight;
            media.style.setProperty('--oy-media-ratio', String(img.naturalWidth / img.naturalHeight));
          }
        });
        media.append(img);
        const caption = document.createElement('figcaption');
        caption.className = 'oy-product__caption';
        panel.append(media, caption);
        this.querySelector('[data-gallery-stage]').prepend(panel);
        this.variantEntry = { id: 'selected-variant', panel, media, img, caption };
      }
      if (this.variantEntry.img.getAttribute('src') !== option.dataset.imageUrl) {
        this.variantEntry.media.style.setProperty('--oy-media-ratio', '1');
        this.variantEntry.img.src = option.dataset.imageUrl;
      }
      this.variantEntry.img.alt = option.dataset.imageAlt || option.dataset.title;
      this.variantEntry.caption.textContent = option.dataset.title;
    }

    advance(direction) {
      if (!this.visibleEntries.length) return;
      const index = this.visibleEntries.findIndex(entry => entry.id === this.currentId);
      const next = (index + direction + this.visibleEntries.length) % this.visibleEntries.length;
      this.selectView(this.visibleEntries[next].id);
    }

    selectView(id) {
      this.currentId = id;
      const allEntries = this.variantEntry ? [...this.entries, this.variantEntry] : this.entries;
      allEntries.forEach(entry => {
        entry.panel.hidden = entry.id !== id;
      });
      const index = this.visibleEntries.findIndex(entry => entry.id === id);
      this.position.textContent = (this.dataset.positionLabel || '').replace('{index}', String(index + 1)).replace('{count}', String(this.visibleEntries.length));
    }
  });
}
